import React, { FunctionComponent, useState } from "react";
import { Form } from "antd";
import { HiOutlineVideoCamera, HiOutlinePlay } from "react-icons/hi";
import dynamic from "next/dynamic";
import FormLabel from "./FormLabel";
import UploadTile from "./UploadTile";
import { buildFormAssetKey, uploadToS3 } from "./s3Upload";
import { QuestionComponentProps } from "../helper";
import { buildRules } from "../formUtils";

// react-media-recorder (recorder modal) and plyr (player) reference browser globals at import time,
// so they must load client-side only — otherwise Next SSR throws "Blob is not defined".
const VideoRecorderModal = dynamic(() => import("./VideoRecorderModal"), { ssr: false });
const VideoPreviewModal = dynamic(() => import("./VideoPreviewModal"), { ssr: false });

interface VideoInputProps {
  value?: string;
  onChange?: (url: string | undefined) => void;
  disabled?: boolean;
  formId?: string;
  questionId?: string;
}

/**
 * Controlled video input. "Record video" opens a modal where the learner sees themselves live,
 * records, reviews the clip in a player, then uploads it to S3. The stored answer is the S3 URL.
 */
const VideoInput: FunctionComponent<VideoInputProps> = ({ value, onChange, disabled, formId, questionId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const upload = (blob: Blob, filename: string, onProgress: (percent: number) => void) =>
    uploadToS3(blob, buildFormAssetKey("video", formId, questionId, filename), blob.type || "video/webm", onProgress);

  const handleUploaded = (url: string) => {
    onChange?.(url);
    setModalOpen(false);
  };

  return (
    <div className="flex flex-col items-start gap-3">
      {value ? (
        // Recorded state: compact inline actions (no inline player — the clip opens in a modal).
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary-1 px-3 py-1 text-sm font-medium text-primary-7 hover:bg-primary-2 focus:outline-none"
          >
            <HiOutlinePlay className="text-base" />
            View recording
          </button>
          {!disabled && (
            <button type="button" onClick={() => setModalOpen(true)} className="text-sm text-primary-6 hover:underline focus:outline-none">
              Re-record
            </button>
          )}
        </div>
      ) : disabled ? (
        <span className="text-sm text-neutral-7">No video found</span>
      ) : (
        <UploadTile icon={<HiOutlineVideoCamera className="text-base" />} label="Record video" disabled={disabled} onClick={() => setModalOpen(true)} />
      )}

      {modalOpen && <VideoRecorderModal open={modalOpen} onCancel={() => setModalOpen(false)} upload={upload} onUploaded={handleUploaded} />}
      {previewOpen && value && <VideoPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} src={value} />}
    </div>
  );
};

// VIDEO — learner records a video from their camera; the answer is the resulting S3 URL.
const Video: FunctionComponent<QuestionComponentProps> = ({ question, disabled = false }) => (
  <Form.Item
    name={question.id}
    label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
    rules={buildRules(question)}
  >
    <VideoInput disabled={disabled} formId={question.formId} questionId={question.id} />
  </Form.Item>
);

export default Video;
